import React, { useState } from 'react';
import { CircleCheck, Loader2 } from 'lucide-react';
import { joinWaitlist } from '../services/poolApi';
import { track, EVENTS } from '../services/analytics';

/* ----------------------------------------------------------------------------
 * WaitlistForm — compact email capture for the marketing pages. Pass the
 * `audience` prop ('candidate' | 'recruiter') to segment the signup. Blue brand,
 * hairline border, inline success state. Matches the Register.js / Join.js look.
 * -------------------------------------------------------------------------- */

const BLUE = '#2563EB';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function WaitlistForm({ audience = 'candidate', className = '' }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!EMAIL_RE.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    try {
      await joinWaitlist(email.trim(), audience);
      track(EVENTS.WAITLIST_JOIN, { audience });
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.error || 'Something went wrong — please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div
        className={`flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-5 py-4 ${className}`}
      >
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          style={{ background: '#EFF4FE', color: BLUE }}
        >
          <CircleCheck size={18} />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">You're on the list</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            We'll be in touch as we open up access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`w-full ${className}`}>
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          aria-label="Email address"
          className="flex-1 appearance-none rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3.5 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm transition-colors focus:border-[#2563EB] focus:outline-none focus:ring-1 focus:ring-[#2563EB]"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          style={{ background: BLUE }}
          onMouseEnter={(e) => status !== 'loading' && (e.currentTarget.style.background = '#1D4FD8')}
          onMouseLeave={(e) => (e.currentTarget.style.background = BLUE)}
        >
          {status === 'loading' ? (
            <>
              <Loader2 size={15} className="animate-spin" /> Joining…
            </>
          ) : (
            'Join the waitlist'
          )}
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </form>
  );
}

export default WaitlistForm;
